/**
 * =============================================================================
 * SCRIPT-NAME:   AdsPerfDashboard.js
 * PROJEKT:       Freiseinapp - Admin-Zentrale
 * VERSION:       v1.3
 * DATUM:         2026-03-03
 *
 * AENDERUNGEN v1.3:
 * - Zweiter Zeitraum (Vergleich) im Filter ergaenzt
 * - Jeder Bereich hat Toggle: Einzeln / Vergleich
 * - Vergleich zeigt Periode1 | Periode2 | Differenz pro Spalte
 * =============================================================================
 */

import React, { useState, useEffect } from 'react';
import "./AdsPerfDashboard.css";
import AdsDecisions from "./AdsDecisions";
import MiniJourney from "./MiniJourney";

const BEREICHE = [
    { key: 'campaigns', label: 'Kampagnen-Uebersicht' },
    { key: 'devices',   label: 'Geraete' },
    { key: 'age',       label: 'Altersgruppen' },
    { key: 'schedule',  label: 'Werbezeitplan' },
    { key: 'keywords',  label: 'Keywords' },
    { key: 'assets',    label: 'Assets' },
];

const API = '/api/ads_perf_report.php';

// Zahlenspalten die verglichen werden sollen
const NUM_COLS = ['Impressionen','Klicks','Kosten','Conversions','CTR_pct','CPC','CPA',
                  'IS_pct','Rank_Lost_pct','Budget_Lost_pct','Impr','Impr_Vorwoche',
                  'Impr_Diff','Klicks_Diff','Kosten_Diff','QF'];

function getDefaultDates() {
    const today = new Date();
    const dow = today.getDay(); // 0=So, 1=Mo, ..., 5=Fr, 6=Sa

    // Letzten Donnerstag berechnen
    // Heute = Freitag (5): letzter Do = gestern (-1)
    // Heute = Samstag (6): letzter Do = vorgestern (-2)
    // Heute = Sonntag (0): letzter Do = -3
    // Heute = Montag (1): letzter Do = -4
    // etc.
    const daysToLastThursday = dow === 4 ? 7 : (dow + 3) % 7 || 7;
    const lastThursday = new Date(today);
    lastThursday.setDate(today.getDate() - daysToLastThursday);

    const lastFriday = new Date(lastThursday);
    lastFriday.setDate(lastThursday.getDate() - 6);

    const prevThursday = new Date(lastFriday);
    prevThursday.setDate(lastFriday.getDate() - 1);

    const prevFriday = new Date(prevThursday);
    prevFriday.setDate(prevThursday.getDate() - 6);

    const fmt = d => d.toISOString().split('T')[0];
    return {
        von1: fmt(lastFriday),
        bis1: fmt(lastThursday),
        von2: fmt(prevFriday),
        bis2: fmt(prevThursday),
    };
}

function isNumCol(col) {
    return NUM_COLS.includes(col);
}

function diff(a, b) {
    const n = parseFloat(a) - parseFloat(b);
    if (isNaN(n)) return '---';
    const sign = n > 0 ? '+' : '';
    return sign + (Number.isInteger(n) ? n : n.toFixed(2));
}

function diffClass(val) {
    const n = parseFloat(val);
    if (isNaN(n) || n === 0) return '';
    return n > 0 ? 'perf-pos' : 'perf-neg';
}

// CTR + CPC berechnen und Bewertung hinzufuegen
function enrichWithCTR(data) {
    if (!data || !data.length) return data;
    const cols = Object.keys(data[0]);
    const hasImpr  = cols.includes('Impressionen');
    const hasClick = cols.includes('Klicks');
    const hasKost  = cols.includes('Kosten');
    if (!hasImpr || !hasClick) return data;

    // Durchschnittliche CTR berechnen (nur Zeilen mit Impressionen > 0)
    const aktive = data.filter(r => parseFloat(r.Impressionen) > 0);
    const avgCTR = aktive.length
        ? aktive.reduce((s, r) => s + (parseFloat(r.Klicks) / parseFloat(r.Impressionen)), 0) / aktive.length
        : 0;

    return data.map(r => {
        const impr   = parseFloat(r.Impressionen) || 0;
        const clicks = parseFloat(r.Klicks) || 0;
        const kosten = parseFloat(r.Kosten) || 0;
        const ctr    = impr > 0 ? (clicks / impr * 100) : null;
        const cpc    = clicks > 0 ? (kosten / clicks) : null;

        let bewertung = '';
        if (impr >= 10 && clicks === 0)        bewertung = 'TOT';
        else if (impr > 0 && ctr < avgCTR * 0.5) bewertung = 'SCHWACH';
        else if (impr > 0 && ctr >= avgCTR * 1.5) bewertung = 'STARK';
        else if (impr > 0)                        bewertung = 'OK';

        return {
            ...r,
            CTR: ctr !== null ? ctr.toFixed(2) + '%' : '---',
            CPC: cpc !== null ? cpc.toFixed(2) + ' €' : '---',
            _bewertung: bewertung,
            _avgCTR: avgCTR,
        };
    });
}

function sortData(data, col, dir) {
    if (!col) return data;
    return [...data].sort((a, b) => {
        const an = parseFloat(a[col]), bn = parseFloat(b[col]);
        if (!isNaN(an) && !isNaN(bn)) return dir === 'asc' ? an - bn : bn - an;
        return dir === 'asc'
            ? String(a[col] ?? '').localeCompare(String(b[col] ?? ''))
            : String(b[col] ?? '').localeCompare(String(a[col] ?? ''));
    });
}

// Merge zwei Datensaetze: UNION (alle Zeilen aus P1 UND P2)
// Fehlende Seite = 0, damit Vergleich vollstaendig ist
function mergeResults(data1, data2, cols) {
    if (!data1.length && !data2.length) return [];

    // Schluessel-Spalten = alle nicht-numerischen Spalten
    const allCols = cols.length ? cols : Object.keys((data1[0] || data2[0] || {}));
    const keyCols = allCols.filter(c => !isNumCol(c));
    const numCols = allCols.filter(c => isNumCol(c));

    // Maps fuer schnellen Lookup
    const map1 = {}, map2 = {};
    data1.forEach(row => { map1[keyCols.map(c => row[c]).join('||')] = row; });
    data2.forEach(row => { map2[keyCols.map(c => row[c]).join('||')] = row; });

    // UNION aller Keys aus P1 + P2
    const allKeys = [...new Set([...Object.keys(map1), ...Object.keys(map2)])];

    return allKeys.map(k => {
        const row1 = map1[k] || {};
        const row2 = map2[k] || {};
        // Schluessel-Werte aus whichever has them
        const base = map1[k] || map2[k];
        const merged = {};
        keyCols.forEach(c => { merged[c] = base[c]; });
        numCols.forEach(c => {
            const v1 = row1[c] != null ? row1[c] : 0;
            const v2 = row2[c] != null ? row2[c] : 0;
            merged[c + '_P1']   = row1[c] != null ? row1[c] : 0;
            merged[c + '_P2']   = row2[c] != null ? row2[c] : 0;
            merged[c + '_DIFF'] = diff(v1, v2);
            // Status-Flag fuer spaetere Analyse-Ebenen
            if (map1[k] && !map2[k]) merged['_status'] = 'NEU';
            else if (!map1[k] && map2[k]) merged['_status'] = 'WEGGEFALLEN';
            else merged['_status'] = 'VORHANDEN';
        });
        return merged;
    });
}

export default function AdsPerfDashboard() {
    const defaults = getDefaultDates();
    const [von1, setVon1]           = useState(defaults.von1);
    const [bis1, setBis1]           = useState(defaults.bis1);
    const [von2, setVon2]           = useState(defaults.von2);
    const [bis2, setBis2]           = useState(defaults.bis2);
    const [campaigns, setCampaigns] = useState([]);
    const [selected, setSelected]   = useState([]);
    const [results, setResults]     = useState({});   // { key: { p1, p2 } }
    const [loading, setLoading]     = useState({});
    const [errors, setErrors]       = useState({});
    const [modus, setModus]         = useState({});   // { key: 'single'|'compare' }
    const [sortBy, setSortBy]       = useState({});
    const [selectedRow, setSelectedRow] = useState(null);

    useEffect(() => {
        const token = localStorage.getItem('token') || '';
        fetch('/api/ads_perf_campaigns_list.php', {
            headers: { 'Authorization': 'Bearer ' + token }
        })
            .then(r => r.json())
            .then(data => {
                setCampaigns(data);
                setSelected(data.map(c => String(c.campaign_id)));
            })
            .catch(() => setCampaigns([]));
    }, []);

    function toggleCampaign(id) {
        const sid = String(id);
        setSelected(prev =>
            prev.includes(sid) ? prev.filter(x => x !== sid) : [...prev, sid]
        );
    }

    function toggleAll() {
        setSelected(selected.length === campaigns.length
            ? [] : campaigns.map(c => String(c.campaign_id)));
    }

    async function fetchBereich(von, bis, key) {
        const token = localStorage.getItem('token') || '';
        const params = new URLSearchParams({
            von, bis,
            campaign_ids: selected.join(','),
            bereich: key
        });
        const res = await fetch(`${API}?${params}`, {
            headers: { 'Authorization': 'Bearer ' + token }
        });
        const data = await res.json();
        if (!data.success) throw new Error(data.message || 'API Fehler');
        return data.data;
    }

    async function loadBereich(key) {
        if (!selected.length) return;
        setLoading(prev => ({ ...prev, [key]: true }));
        setErrors(prev => ({ ...prev, [key]: null }));
        try {
            const p1 = await fetchBereich(von1, bis1, key);
            const p2 = await fetchBereich(von2, bis2, key);
            setResults(prev => ({ ...prev, [key]: { p1, p2 } }));
        } catch (e) {
            setErrors(prev => ({ ...prev, [key]: e.message || 'Fehler beim Laden.' }));
        } finally {
            setLoading(prev => ({ ...prev, [key]: false }));
        }
    }

    function handleSort(bereichKey, col) {
        setSortBy(prev => {
            const cur = prev[bereichKey] || { col: null, dir: 'asc' };
            const dir = cur.col === col && cur.dir === 'asc' ? 'desc' : 'asc';
            return { ...prev, [bereichKey]: { col, dir } };
        });
    }

    function renderSingleTable(data, bereichKey) {
        if (!data || !data.length) return <p className="perf-empty">Keine Daten.</p>;
        const cols = Object.keys(data[0]);
        const sort = sortBy[bereichKey] || { col: null, dir: 'asc' };
        const sorted   = sortData(data, sort.col, sort.dir);
        const visCols  = cols.filter(c => !c.startsWith('_'));
        return (
            <div className="perf-table-wrap">
                <table className="perf-table">
                    <thead>
                        <tr>{visCols.map(c => (
                            <th key={c} className="perf-th-sortable" onClick={() => handleSort(bereichKey, c)}>
                                {c}{sort.col === c ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ' ⇅'}
                            </th>
                        ))}</tr>
                    </thead>
                    <tbody>
                        {sorted.map((row, i) => {
                            const bew = row._bewertung || '';
                            const rowCls = bew === 'TOT'    ? 'row-tot'
                                         : bew === 'SCHWACH' ? 'row-schwach'
                                         : bew === 'STARK'   ? 'row-stark'
                                         : '';
                            return (
                                <tr key={i}
                                    className={[rowCls, selectedRow === `${bereichKey}-${i}` ? 'row-selected' : ''].filter(Boolean).join(' ')}
                                    onClick={() => setSelectedRow(selectedRow === `${bereichKey}-${i}` ? null : `${bereichKey}-${i}`)}>
                                    {visCols.map(c => <td key={c}>{row[c] ?? '---'}</td>)}

                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    }

    function renderCompareTable(p1, p2, bereichKey) {
        if (!p1 || !p1.length) return <p className="perf-empty">Keine Daten fuer Zeitraum 1.</p>;
        const baseCols = Object.keys(p1[0]);
        const merged   = mergeResults(p1, p2, baseCols);
        if (!merged.length) return <p className="perf-empty">Keine Daten.</p>;

        const cols     = Object.keys(merged[0]);
        const sort     = sortBy[bereichKey] || { col: null, dir: 'asc' };
        const sorted   = sortData(merged, sort.col, sort.dir);

        // Spaltenheader schoener machen
        function colLabel(c) {
            if (c.endsWith('_P1'))   return c.replace('_P1','') + ' (Z1)';
            if (c.endsWith('_P2'))   return c.replace('_P2','') + ' (Z2)';
            if (c.endsWith('_DIFF')) return c.replace('_DIFF','') + ' +/-';
            return c;
        }

        // Analyse-Zusammenfassung berechnen (nur VORHANDEN fuer Gewinner/Verlierer)
        const imprDiffCol = cols.find(c => c.endsWith('_DIFF') && c.includes('Impr'));
        const vorhandene  = merged.filter(r => r._status === 'VORHANDEN');
        const summary = {
            neu:         merged.filter(r => r._status === 'NEU').length,
            weggefallen: merged.filter(r => r._status === 'WEGGEFALLEN').length,
            gewinner:    vorhandene.filter(r => imprDiffCol && parseFloat(r[imprDiffCol]) > 0).length,
            verlierer:   vorhandene.filter(r => imprDiffCol && parseFloat(r[imprDiffCol]) < 0).length,
        };

        const visibleCols = cols.filter(c => c !== '_status');

        return (
            <div>
                <div className="perf-analysis-bar">
                    {summary.neu > 0 && <span className="badge badge-new">🆕 {summary.neu} Neu</span>}
                    {summary.weggefallen > 0 && <span className="badge badge-gone">🚫 {summary.weggefallen} Weggefallen</span>}
                    {summary.gewinner > 0 && <span className="badge badge-win">📈 {summary.gewinner} Gewinner</span>}
                    {summary.verlierer > 0 && <span className="badge badge-lose">📉 {summary.verlierer} Verlierer</span>}
                </div>
                <div className="perf-table-wrap">
                    <table className="perf-table">
                        <thead>
                            <tr>
                                <th className="perf-th-sortable" onClick={() => handleSort(bereichKey, '_status')}>Status</th>
                                {visibleCols.map(c => (
                                    <th key={c} className={`perf-th-sortable${c.endsWith('_DIFF') ? ' perf-th-diff' : ''}`}
                                        onClick={() => handleSort(bereichKey, c)}>
                                        {colLabel(c)}{sort.col === c ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ' ⇅'}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {sorted.map((row, i) => {
                                const status = row._status || '';
                                const rowCls = status === 'NEU' ? 'row-new'
                                             : status === 'WEGGEFALLEN' ? 'row-gone' : '';
                                return (
                                    <tr key={i}
                                        className={[rowCls, selectedRow === `cmp-${bereichKey}-${i}` ? 'row-selected' : ''].filter(Boolean).join(' ')}
                                        onClick={() => setSelectedRow(selectedRow === `cmp-${bereichKey}-${i}` ? null : `cmp-${bereichKey}-${i}`)}>
                                        <td>
                                            {status === 'NEU' && <span className="badge badge-new">Neu</span>}
                                            {status === 'WEGGEFALLEN' && <span className="badge badge-gone">Weg</span>}
                                            {status === 'VORHANDEN' && <span className="badge badge-ok">✓</span>}
                                        </td>
                                        {visibleCols.map(c => {
                                            const v = row[c] ?? '---';
                                            const cls = c.endsWith('_DIFF') ? diffClass(v) : '';
                                            return <td key={c} className={cls}>{v}</td>;
                                        })}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    function renderAssetsTables(data) {
        if (!data || typeof data !== 'object') return <p className="perf-empty">Keine Daten.</p>;
        const { titles = [], texts = [], sitelinks = [] } = data;
        const enrichedTitles = enrichWithCTR(titles);
        const enrichedTexts  = enrichWithCTR(texts);

        // Bewertungs-Zusammenfassung
        function assetSummary(rows) {
            const tot    = rows.filter(r => r._bewertung === 'TOT').length;
            const schwach = rows.filter(r => r._bewertung === 'SCHWACH').length;
            const stark  = rows.filter(r => r._bewertung === 'STARK').length;
            return (
                <div className="perf-analysis-bar">
                    {stark  > 0 && <span className="badge badge-win">⭐ {stark} Stark</span>}
                    {schwach > 0 && <span className="badge badge-lose">⚠️ {schwach} Schwach</span>}
                    {tot    > 0 && <span className="badge badge-gone">💀 {tot} Tot (&gt;10 Impr, 0 Klicks)</span>}
                </div>
            );
        }

        return (
            <div>
                <h4 className="perf-sub-heading">Titel ({enrichedTitles.length})</h4>
                {assetSummary(enrichedTitles)}
                {renderSingleTable(enrichedTitles, 'assets_titles')}
                <AdsDecisions bereich="assets_titles" selectedIds={selected} campaignList={campaigns} />
                <h4 className="perf-sub-heading">Texte ({enrichedTexts.length})</h4>
                {assetSummary(enrichedTexts)}
                {renderSingleTable(enrichedTexts, 'assets_texts')}
                <AdsDecisions bereich="assets_texts" selectedIds={selected} campaignList={campaigns} />
                <h4 className="perf-sub-heading">Sitelinks ({sitelinks.length})</h4>
                {renderSingleTable(sitelinks, 'assets_sitelinks')}
                <AdsDecisions bereich="assets_sitelinks" selectedIds={selected} campaignList={campaigns} />
            </div>
        );
    }

    function renderAssetsCompare(p1, p2) {
        if (!p1 || typeof p1 !== 'object') return <p className="perf-empty">Keine Daten.</p>;
        const sections = [
            { label: 'Titel',    d1: p1.titles    || [], d2: p2?.titles    || [], key: 'cmp_titles' },
            { label: 'Texte',    d1: p1.texts     || [], d2: p2?.texts     || [], key: 'cmp_texts' },
            { label: 'Sitelinks',d1: p1.sitelinks || [], d2: p2?.sitelinks || [], key: 'cmp_sitelinks' },
        ];
        return (
            <div>
                {sections.map(({ label, d1, d2, key }) => (
                    <div key={key}>
                        <h4 className="perf-sub-heading">{label} ({d1.length})</h4>
                        {renderCompareTable(d1, d2, key)}
                    </div>
                ))}
            </div>
        );
    }

    return (
        <div className="perf-page">
            <div className="perf-box">

                <header className="perf-header">
                    <h2>Ads Performance Dashboard</h2>
                </header>

                <section className="perf-filter">
                    {/* Zeitraum 1 */}
                    <div className="perf-filter-row">
                        <span className="perf-period-label">Zeitraum 1</span>
                        <label>Von</label>
                        <input type="date" value={von1} onChange={e => setVon1(e.target.value)} />
                        <label>Bis</label>
                        <input type="date" value={bis1} onChange={e => setBis1(e.target.value)} />
                    </div>
                    {/* Zeitraum 2 */}
                    <div className="perf-filter-row">
                        <span className="perf-period-label">Zeitraum 2</span>
                        <label>Von</label>
                        <input type="date" value={von2} onChange={e => setVon2(e.target.value)} />
                        <label>Bis</label>
                        <input type="date" value={bis2} onChange={e => setBis2(e.target.value)} />
                    </div>

                    {/* Kampagnen */}
                    <div className="perf-campaigns">
                        <div className="perf-campaigns-header">
                            <span className="perf-filter-label">Kampagnen</span>
                            <button className="perf-btn-small" onClick={toggleAll}>
                                {selected.length === campaigns.length ? 'Alle ab' : 'Alle an'}
                            </button>
                        </div>
                        <div className="perf-campaign-list">
                            {campaigns.map(c => (
                                <label key={c.campaign_id} className="perf-campaign-item">
                                    <input
                                        type="checkbox"
                                        checked={selected.includes(String(c.campaign_id))}
                                        onChange={() => toggleCampaign(c.campaign_id)}
                                    />
                                    {c.campaign_name}
                                </label>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Mini Journey — oberhalb Geräte */}
                <div className="perf-section">
                    <MiniJourney
                        von1={von1}
                        bis1={bis1}
                        campaignIds={selected}
                    />
                    <AdsDecisions bereich="journey" selectedIds={selected} campaignList={campaigns} />
                </div>

                {BEREICHE.map(({ key, label }) => {
                    const mod = modus[key] || 'single';
                    const res = results[key];
                    return (
                        <section key={key} className="perf-section">
                            <div className="perf-section-header">
                                <h3>{label}</h3>
                                <div className="perf-section-controls">
                                    {res && (
                                        <>
                                            <button
                                                className={`perf-btn-small ${mod === 'single' ? 'active' : ''}`}
                                                onClick={() => setModus(prev => ({ ...prev, [key]: 'single' }))}
                                            >Einzeln</button>
                                            <button
                                                className={`perf-btn-small ${mod === 'compare' ? 'active' : ''}`}
                                                onClick={() => setModus(prev => ({ ...prev, [key]: 'compare' }))}
                                            >Vergleich</button>
                                        </>
                                    )}
                                    <button
                                        className="perf-btn"
                                        onClick={() => loadBereich(key)}
                                        disabled={loading[key] || !selected.length}
                                    >
                                        {loading[key] ? 'Laedt...' : 'Laden'}
                                    </button>
                                </div>
                            </div>
                            {errors[key] && <p className="perf-error">{errors[key]}</p>}
                            {res && mod === 'single'  && (
                                key === 'assets'
                                    ? renderAssetsTables(res.p1)
                                    : renderSingleTable(res.p1, key)
                            )}
                            {res && mod === 'compare' && (
                                key === 'assets'
                                    ? renderAssetsCompare(res.p1, res.p2)
                                    : renderCompareTable(res.p1, res.p2, key)
                            )}
                            {res && key !== 'assets' && (
                                <AdsDecisions
                                    bereich={key}
                                    selectedIds={selected}
                                    campaignList={campaigns}
                                    kontext=""
                                />
                            )}
                        </section>
                    );
                })}

                <div className="perf-footer">
                    <a href="/admin" className="perf-btn" style={{textDecoration:'none'}}>← Zurück zum Dashboard</a>
                </div>

            </div>
        </div>
    );
}